import { useCallback, useEffect, useMemo, useRef } from 'react';

import { ApplicationEdition } from '@atlassian/invite-people/types';
import debounce from 'lodash.debounce';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

import { useAnalytics } from '@townsquare/analytics';

import { SpaceSelectOption } from '../types/SpaceInputTypes';

import type {
	useSpaceSearchQuery as UseSpaceSearchQueryType,
	JiraProjectType,
} from './__generated__/useSpaceSearchQuery.graphql';

export type JiraProject = NonNullable<
	NonNullable<
		NonNullable<
			NonNullable<UseSpaceSearchQueryType['response']['jira']>['allJiraProjects']
		>['edges']
	>[number]
>['node'];

const DEBOUNCE_TIMEOUT = 200;

export const PROJECT_TYPE_MAP: Record<string, 'software' | 'business' | null> = {
	SOFTWARE: 'software',
	BUSINESS: 'business',
};

export const getProjectType = (
	projectType: JiraProjectType | null | undefined,
): 'software' | 'business' | null => (projectType ? (PROJECT_TYPE_MAP[projectType] ?? null) : null);

export const shouldIncludeProject = (
	projectType: JiraProjectType | null | undefined,
	projectStyle: string | null | undefined,
	appEdition: ApplicationEdition,
): boolean => {
	// filtering out CMP free projects, the context is that cmp project does not have default roles
	if (appEdition === 'FREE_EDITION' && projectStyle === 'COMPANY_MANAGED_PROJECT') {
		return false;
	}
	return projectType === 'SOFTWARE' || projectType === 'BUSINESS';
};

export const createSpaceOption = ({
	projectId,
	key,
	name,
	projectType,
	projectStyle,
	avatarUrl,
	source,
}: {
	projectId: string;
	key: string;
	name: string;
	projectType: JiraProjectType | null | undefined;
	projectStyle: string | null | undefined;
	avatarUrl: string | null | undefined;
	source: 'recommendation' | 'project-list' | 'search';
}): SpaceSelectOption => ({
	label: name,
	value: projectId,
	data: {
		id: projectId,
		key,
		name,
		projectTypeKey: getProjectType(projectType),
		projectStyle:
			projectStyle === 'COMPANY_MANAGED_PROJECT' || projectStyle === 'TEAM_MANAGED_PROJECT'
				? projectStyle
				: null,
		source,
		avatarUrls: avatarUrl ? { '24x24': avatarUrl } : undefined,
	},
});

export const buildProjectOptions = (
	nodes: ReadonlyArray<JiraProject | null | undefined> | null | undefined,
	appEdition: ApplicationEdition,
	excludeProjectIds?: Set<string>,
	source: 'recommendation' | 'project-list' | 'search' = 'project-list',
): SpaceSelectOption[] =>
	nodes?.flatMap((node) => {
		if (!node?.name || !node.projectId || !node.key) return [];
		if (excludeProjectIds?.has(node.projectId)) return [];
		if (!shouldIncludeProject(node.projectType, node.projectStyle, appEdition)) return [];

		return [
			createSpaceOption({
				projectId: node.projectId,
				key: node.key,
				name: node.name,
				projectType: node.projectType,
				projectStyle: node.projectStyle,
				avatarUrl: node.avatar?.medium,
				source,
			}),
		];
	}) ?? [];

export type UseSpaceSearchParams = {
	cloudId: string;
	appEdition: ApplicationEdition;
	defaultOptions: SpaceSelectOption[];
};

export const useSpaceSearch = ({ cloudId, appEdition, defaultOptions }: UseSpaceSearchParams) => {
	const environment = useRelayEnvironment();
	const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
	const analytics = useAnalytics();

	const searchSpaces = useCallback(
		(keyword: string): Promise<SpaceSelectOption[]> => {
			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}

			void analytics.ui(
				'option',
				'searched',
				{
					keyword,
					appEdition,
				},
				'spacePicker',
			);

			return new Promise((resolve) => {
				const subscription = fetchQuery<UseSpaceSearchQueryType>(
					environment,
					graphql`
						query useSpaceSearchQuery($cloudId: ID!, $keyword: String, $first: Int) {
							jira {
								allJiraProjects(
									cloudId: $cloudId
									filter: { types: [BUSINESS, SOFTWARE], keyword: $keyword }
									first: $first
								) {
									edges {
										node {
											projectId
											name
											key
											avatar {
												medium
											}
											projectType
											projectStyle
										}
									}
								}
							}
						}
					`,
					{ cloudId, keyword, first: 20 },
					{ fetchPolicy: 'store-or-network' },
				).subscribe({
					next: (result) => {
						subscriptionRef.current = null;
						const nodes = result.jira?.allJiraProjects?.edges?.map((e) => e?.node);
						const projectOptions = buildProjectOptions(nodes, appEdition, undefined, 'search');
						const finalOptions = projectOptions.slice(0, 10);
						resolve(finalOptions);
					},
					error: () => {
						subscriptionRef.current = null;
						void analytics.track('spacePicker', 'search-failed', {
							keyword,
							appEdition,
						});
						resolve([]);
					},
				});

				subscriptionRef.current = subscription;
			});
		},
		[environment, cloudId, appEdition, analytics],
	);

	const debouncedSearch = useMemo(
		() =>
			debounce((keyword: string, callback: (options: SpaceSelectOption[]) => void) => {
				searchSpaces(keyword).then(callback);
			}, DEBOUNCE_TIMEOUT),
		[searchSpaces],
	);

	const loadOptions = useCallback(
		(inputValue: string, callback: (options: SpaceSelectOption[]) => void) => {
			const keyword = inputValue.trim();

			if (keyword === '') {
				callback(defaultOptions);
				return;
			}

			debouncedSearch(keyword, callback);
		},
		[defaultOptions, debouncedSearch],
	);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();

			if (subscriptionRef.current) {
				subscriptionRef.current.unsubscribe();
				subscriptionRef.current = null;
			}
		};
	}, [debouncedSearch]);

	return loadOptions;
};
